import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Badge, Spinner, ProgressBar, Button } from 'react-bootstrap';
import BrokerApiService from '../../services/brokerApiService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faDollarSign, 
  faChartLine, 
  faArrowUp, 
  faArrowDown,
  faBolt,
  faRocket,
  faGraduationCap,
  faCrown,
  faBuilding,
  faCalendarAlt,
  faArrowTrendUp,
  faHandHoldingDollar,
  faRobot,
  faTasks,
  faUserClock,
  faHandshake,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faChartPie
} from '@fortawesome/free-solid-svg-icons';

interface TransactionData {
  totalTransactions: number;
  aiCoordinatorActive: number;
  averageTasksAutomated: number;
  hoursFreedPerAgent: number;
  costSavingsVsHuman: number;
  complianceScore: number;
}

interface ActivityItem {
  type: 'coordinator_activated' | 'transaction_closed' | 'deadline_flagged' | 'milestone_achieved';
  agent: string;
  description: string;
  timestamp: string;
  impact?: string;
}

interface EnhancedDashboardData {
  // Transaction AI Metrics
  transactionData: TransactionData;
  
  // Team Efficiency
  communicationEfficiency: number;
  responseRate: number;
  avgResponseTime: string;
  
  // Agent Performance
  agentProductivity: number;
  transactionsPerAgent: number;
  additionalGCIPerAgent: number;
  
  // Financial Impact
  platformInvestment: number;
  aiCoordinatorFees: number;
  totalROI: number;
  marketShareGain: number;
  
  // Activity Stream
  recentActivity: ActivityItem[];
  
  // Original metrics
  totalAgents: number;
  activeAgents: number;
  upgradeRate: number;
  planUtilization: number;
}

interface BrokerDashboardEnhancedProps {
  brokerageId?: string;
  loading?: boolean;
  useNewMessaging?: boolean; // Feature flag
}
// Utility function to format timestamps
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString();
};

const BrokerDashboardEnhanced: React.FC<BrokerDashboardEnhancedProps> = ({ 
  brokerageId, 
  loading = false,
  useNewMessaging = true
}) => {
  const [dashboardData, setDashboardData] = useState<EnhancedDashboardData>({
    transactionData: {
      totalTransactions: 15,
      aiCoordinatorActive: 12,
      averageTasksAutomated: 47,
      hoursFreedPerAgent: 18,
      costSavingsVsHuman: 18000,
      complianceScore: 98
    },
    communicationEfficiency: 94,
    responseRate: 94,
    avgResponseTime: '2.3hr',
    agentProductivity: 3.2,
    transactionsPerAgent: 2.8,
    additionalGCIPerAgent: 5805,
    platformInvestment: 7400,
    aiCoordinatorFees: 5400,
    totalROI: 26.3,
    marketShareGain: 4.2,
    recentActivity: [],
    totalAgents: 34,
    activeAgents: 34,
    upgradeRate: 75,
    planUtilization: 32
  });
  
  const [error, setError] = useState<string | null>(null);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!brokerageId || loading) return;
      
      try {
        setDataLoading(true);
        
        // Fetch all metrics in parallel
        const [transactionMetrics, communicationMetrics, activityStream] = await Promise.allSettled([
          BrokerApiService.getTransactionAIMetrics(brokerageId),
          BrokerApiService.getCommunicationMetrics(brokerageId),
          BrokerApiService.getActivityStream(brokerageId, 10)
        ]);

        // Update dashboard data with API responses
        setDashboardData(prevData => {
          const newData = { ...prevData };

          // Process transaction metrics
          if (transactionMetrics.status === 'fulfilled' && transactionMetrics.value.success) {
            const metrics = transactionMetrics.value.metrics;
            newData.transactionData = {
              totalTransactions: metrics.totalTransactions,
              aiCoordinatorActive: metrics.aiCoordinatorActive,
              averageTasksAutomated: metrics.averageTasksAutomated,
              hoursFreedPerAgent: metrics.hoursFreedPerAgent,
              costSavingsVsHuman: metrics.costSavingsVsHuman,
              complianceScore: metrics.complianceScore
            };
            
            if (metrics.roiMetrics) {
              newData.platformInvestment = metrics.roiMetrics.platformInvestment;
              newData.aiCoordinatorFees = metrics.roiMetrics.aiCoordinatorFees;
              newData.totalROI = metrics.roiMetrics.roi;
            }
          }

          // Process communication metrics
          if (communicationMetrics.status === 'fulfilled' && communicationMetrics.value.success) {
            const metrics = communicationMetrics.value.metrics;
            newData.communicationEfficiency = metrics.responseRate;
            newData.responseRate = metrics.responseRate;
            newData.avgResponseTime = metrics.avgResponseTime;
          }

          // Process activity stream
          if (activityStream.status === 'fulfilled' && activityStream.value.success) {
            newData.recentActivity = activityStream.value.activities;
          }

          return newData;
        });

        setError(null);
      } catch (err: any) {
        console.error('Error fetching dashboard data:', err);
        setError('Unable to load some dashboard data. Using cached values.');
      } finally {
        setDataLoading(false);
      }
    };

    fetchDashboardData();
    
    // Refresh data every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    
    return () => clearInterval(interval);
  }, [brokerageId, loading]);

  // Transaction Intelligence Card - PRIMARY FOCUS
  const TransactionIntelligenceCard: React.FC = () => (
    <Card 
      className="h-100 border-0 ai-hover-lift"
      style={{
        background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
        border: '2px solid rgba(0, 212, 255, 0.3)',
        borderRadius: '16px',
        backdropFilter: 'blur(10px)',
        minHeight: '250px'
      }}
    >
      <Card.Body className="p-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="fw-bold mb-1" style={{ color: '#00d4ff' }}>
              AI Transaction Coordinators Active
            </h4>
            <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
              Handling details while agents focus on clients
            </p>
          </div>
          <div 
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, #00d4ff, #10b981)',
              boxShadow: '0 0 30px rgba(0, 212, 255, 0.5)'
            }}
          >
            <FontAwesomeIcon 
              icon={faRobot} 
              className="text-white" 
              size="xl"
              style={{ filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.8))' }}
            />
          </div>
        </div>

        <div className="row g-3">
          <div className="col-6">
            <div 
              className="text-center p-3 rounded"
              style={{ background: 'rgba(255, 255, 255, 0.05)' }}
            >
              <h2 className="fw-bold mb-1" style={{ color: '#00d4ff', fontSize: '2.5rem' }}>
                {dashboardData.transactionData.aiCoordinatorActive}
              </h2>
              <small className="text-muted">AI Agents Working</small>
            </div>
          </div>
          <div className="col-6">
            <div 
              className="text-center p-3 rounded"
              style={{ background: 'rgba(255, 255, 255, 0.05)' }}
            >
              <h2 className="fw-bold mb-1" style={{ color: '#10b981', fontSize: '2.5rem' }}>
                {dashboardData.transactionData.averageTasksAutomated}
              </h2>
              <small className="text-muted">Tasks per Deal</small>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted small">AI Adoption Rate</span>
            <span className="fw-bold" style={{ color: '#00d4ff' }}>
              {Math.round((dashboardData.transactionData.aiCoordinatorActive / dashboardData.transactionData.totalTransactions) * 100)}%
            </span>
          </div>
          <ProgressBar 
            now={(dashboardData.transactionData.aiCoordinatorActive / dashboardData.transactionData.totalTransactions) * 100}
            style={{ height: '8px', background: 'rgba(0, 212, 255, 0.1)' }}
          >
            <ProgressBar 
              striped 
              animated 
              now={(dashboardData.transactionData.aiCoordinatorActive / dashboardData.transactionData.totalTransactions) * 100}
              style={{ background: 'linear-gradient(90deg, #00d4ff, #10b981)' }}
            />
          </ProgressBar>
        </div>

        <div className="mt-3 p-3 rounded" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
          <div className="d-flex justify-content-between align-items-center">
            <span className="small">Cost Savings vs Human Coordinators</span>
            <span className="fw-bold" style={{ color: '#10b981' }}>
              ${dashboardData.transactionData.costSavingsVsHuman.toLocaleString()}/mo
            </span>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  // Team Collaboration Index Card
  const TeamCollaborationCard: React.FC = () => (
    <Card 
      className="h-100 border-0 ai-hover-lift"
      style={{
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '16px',
        backdropFilter: 'blur(10px)'
      }}
    >
      <Card.Body className="p-4">
        <div className="d-flex align-items-center mb-3">
          <FontAwesomeIcon 
            icon={faHandshake} 
            className="me-2" 
            style={{ color: '#3b82f6', fontSize: '20px' }}
          />
          <h5 className="mb-0 fw-semibold">Team Collaboration Index</h5>
        </div>

        <div className="text-center my-4">
          <h1 className="fw-bold mb-1" style={{ color: '#3b82f6', fontSize: '3rem' }}>
            {dashboardData.communicationEfficiency}%
          </h1>
          <p className="text-muted mb-0">Response Rate</p>
          <small className="text-muted">{dashboardData.avgResponseTime} avg response time</small>
        </div>

        <div className="mt-4">
          <Badge bg="info" className="w-100 py-2" style={{ fontSize: '12px' }}>
            Outperforming 87% of brokerages
          </Badge>
        </div>
      </Card.Body>
    </Card>
  );

  // Agent Productivity Score Card
  const AgentProductivityCard: React.FC = () => (
    <Card 
      className="h-100 border-0 ai-hover-lift"
      style={{
        background: 'rgba(245, 158, 11, 0.1)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: '16px',
        backdropFilter: 'blur(10px)'
      }}
    >
      <Card.Body className="p-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="mb-0 fw-semibold">Agent Performance</h5>
          <FontAwesomeIcon 
            icon={faChartLine} 
            style={{ color: '#f59e0b', fontSize: '20px' }}
          />
        </div>

        <div className="mb-4">
          <h2 className="fw-bold mb-1" style={{ color: '#f59e0b' }}>
            {dashboardData.agentProductivity}x
          </h2>
          <p className="text-muted mb-0">Productivity Gain</p>
          <small className="text-muted">Since AI tool adoption</small>
        </div>

        <div className="border-top pt-3">
          <div className="d-flex justify-content-between mb-2">
            <small className="text-muted">Transactions/Agent</small>
            <small className="fw-bold">{dashboardData.transactionsPerAgent}/mo</small>
          </div>
          <div className="d-flex justify-content-between">
            <small className="text-muted">Additional GCI</small>
            <small className="fw-bold" style={{ color: '#f59e0b' }}>
              +${dashboardData.additionalGCIPerAgent}/agent
            </small>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  // Financial Impact Card
  const FinancialImpactCard: React.FC = () => (
    <Card 
      className="h-100 border-0 ai-hover-lift"
      style={{
        background: 'rgba(16, 185, 129, 0.1)',
        border: '1px solid rgba(16, 185, 129, 0.3)',
        borderRadius: '16px',
        backdropFilter: 'blur(10px)'
      }}
    >
      <Card.Body className="p-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="mb-0 fw-semibold">Financial Impact</h5>
          <FontAwesomeIcon 
            icon={faDollarSign} 
            style={{ color: '#10b981', fontSize: '20px' }}
          />
        </div>

        <div className="text-center mb-4">
          <h1 className="fw-bold mb-1" style={{ color: '#10b981', fontSize: '2.5rem' }}>
            {dashboardData.totalROI}x ROI
          </h1>
          <p className="text-muted mb-0">Return on Platform Investment</p>
        </div>

        <div className="bg-white bg-opacity-50 rounded p-3">
          <div className="d-flex justify-content-between mb-2">
            <small>Platform Investment</small>
            <small className="fw-bold">${dashboardData.platformInvestment}/mo</small>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <small>AI Coordinator Fees</small>
            <small className="fw-bold">${dashboardData.aiCoordinatorFees}/mo</small>
          </div>
          <hr className="my-2" />
          <div className="d-flex justify-content-between">
            <small className="fw-bold">Market Share Gain</small>
            <small className="fw-bold" style={{ color: '#10b981' }}>
              +{dashboardData.marketShareGain}%
            </small>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  // Activity Stream Component
  const ActivityStream: React.FC = () => {
    // Use real activity data from API or fallback to examples
    const activityItems: ActivityItem[] = dashboardData.recentActivity.length > 0 
      ? dashboardData.recentActivity.map(activity => ({
          type: activity.type,
          agent: activity.agent,
          description: activity.description,
          timestamp: formatTimestamp(activity.timestamp),
          impact: activity.impact
        }))
      : [
          {
            type: 'coordinator_activated',
            agent: 'Sarah Johnson',
            description: 'activated AI coordinator',
            timestamp: '2 min ago',
            impact: 'Handling 47 tasks automatically'
          },
          {
            type: 'transaction_closed',
            agent: 'Mike Chen',
            description: 'closed transaction',
            timestamp: '15 min ago',
            impact: 'AI coordinator managed all deadlines & compliance'
          },
          {
            type: 'deadline_flagged',
            agent: 'Emma Davis',
            description: 'AI flagged critical inspection deadline',
            timestamp: '1 hour ago'
          },
          {
            type: 'milestone_achieved',
            agent: 'Team',
            description: '75% of agents using AI coordinators',
            timestamp: '2 hours ago',
            impact: 'Saving $500-2000 per transaction'
          }
        ];

    const getActivityIcon = (type: ActivityItem['type']) => {
      switch (type) {
        case 'coordinator_activated': return faRocket;
        case 'transaction_closed': return faCheckCircle;
        case 'deadline_flagged': return faExclamationTriangle;
        case 'milestone_achieved': return faTasks;
        default: return faClock;
      }
    };

    const getActivityColor = (type: ActivityItem['type']) => {
      switch (type) {
        case 'coordinator_activated': return '#00d4ff';
        case 'transaction_closed': return '#10b981';
        case 'deadline_flagged': return '#f59e0b';
        case 'milestone_achieved': return '#3b82f6';
        default: return '#6b7280';
      }
    };

    return (
      <Card 
        className="h-100 border-0"
        style={{
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Card.Header 
          className="border-bottom-0 py-3"
          style={{ background: 'transparent' }}
        >
          <h5 className="mb-0 fw-semibold">Activity Feed - Intelligence Stream</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="activity-stream">
            {activityItems.map((item, index) => (
              <div 
                key={index} 
                className="p-3 border-bottom d-flex align-items-start"
                style={{ borderColor: 'rgba(255, 255, 255, 0.1) !important' }}
              >
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                  style={{
                    width: '32px',
                    height: '32px',
                    background: `${getActivityColor(item.type)}20`,
                    border: `2px solid ${getActivityColor(item.type)}40`
                  }}
                >
                  <FontAwesomeIcon 
                    icon={getActivityIcon(item.type)} 
                    style={{ color: getActivityColor(item.type), fontSize: '14px' }}
                  />
                </div>
                <div className="flex-grow-1">
                  <p className="mb-1">
                    <strong>{item.agent}</strong> {item.description}
                  </p>
                  {item.impact && (
                    <small className="text-muted d-block">{item.impact}</small>
                  )}
                  <small className="text-muted">{item.timestamp}</small>
                </div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Main render
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-5">
        <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="text-warning mb-3" />
        <h4>Unable to Load Dashboard</h4>
        <p className="text-muted">{error}</p>
        <Button variant="primary" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="broker-dashboard-enhanced">
      {/* Daily Intelligence Brief */}
      <Card 
        className="mb-4 border-0"
        style={{
          background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
          borderRadius: '16px',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Card.Body className="p-4">
          <h4 className="fw-bold mb-3">Good morning! 👋</h4>
          <Row>
            <Col md={6}>
              <h6 className="fw-semibold mb-2">TODAY'S INTELLIGENCE:</h6>
              <ul className="small mb-0">
                <li>3 transactions closing this week (2 with AI coordinators)</li>
                <li>AI agents handled 141 tasks yesterday, freeing 47 agent hours</li>
                <li>Office communication efficiency: 94% (Top 5% nationally)</li>
                <li>Growth opportunity: 4 agents ready for AI coordinators</li>
              </ul>
            </Col>
            <Col md={6}>
              <h6 className="fw-semibold mb-2">COMPETITIVE EDGE:</h6>
              <p className="small mb-3">
                Your agents deliver superior client service with AI coordinators 
                handling all transaction details. They manage 2-3x more deals while 
                maintaining professional standards that competitors can't match.
              </p>
              <div>
                <Button variant="primary" size="sm" className="me-2">
                  View Full Intelligence Report
                </Button>
                <Button variant="outline-primary" size="sm">
                  Activate AI for More Agents
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Primary Metrics */}
      <Row className="g-4 mb-4">
        <Col xl={6}>
          <TransactionIntelligenceCard />
        </Col>
        <Col xl={6}>
          <Row className="g-4">
            <Col md={6}>
              <TeamCollaborationCard />
            </Col>
            <Col md={6}>
              <AgentProductivityCard />
            </Col>
            <Col md={12}>
              <FinancialImpactCard />
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Activity Stream */}
      <Row>
        <Col>
          <ActivityStream />
        </Col>
      </Row>
    </div>
  );
};

export default BrokerDashboardEnhanced;